import {
  Container,
  Box,
  Heading,
  Text,
  Button,
  Icon,
  ButtonProps,
  Flex,
  Modal,
  useDisclosure,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Link,
} from '@everlywell/ui-kit';
import { ArrowRight } from '@phosphor-icons/react';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import { createSharedButtonProps } from 'components/PageSections/utils';
import PricingCard, { PricingCardProps } from 'components/PricingCard';
import { MembershipOptionType } from 'components/PurchaseOptionsDrawerProvider/components/PurchaseOptionsDrawer';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import React, { useEffect, useState } from 'react';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { RESULTS_ROOT } from 'utils/constants/urls';
import { shouldUrlSkipScreenerModal } from 'utils/helpers/urls/skipModal';
import useMdpModals from 'utils/hooks/useMdpModals';

import PricingSelectorModal from './PricingSelectorModal';
import { pricingModalParser } from './PricingSelectorModal/PricingSelectorModal.parser';

// Types ------

type ExperimentModal = {
  title?: string;
  description?: string;
  cta?: Queries.ContentfulCallToAction001;
  isExperimentModal: boolean;
};

// Type guard to check if the modal is an ExperimentModal
const isExperimentModal = (
  modal: ExperimentModal | Queries.ContentfulModal001,
): modal is ExperimentModal =>
  'isExperimentModal' in modal && modal.isExperimentModal === true;

const isPricingSelectorModal = (
  modal: ExperimentModal | Queries.ContentfulModal001,
): modal is Queries.ContentfulModal001 =>
  !isExperimentModal(modal) && modal?.componentName === 'PricingSelectorModal';

export interface PricingSelectorProps {
  CTALabel: string;
  CTAUrl?: string;
  backgroundColor?: string;
  disclaimer?: string;
  eyebrow: string;
  options: PricingCardProps[];
  sectionId?: string;
  selectedOptionCallback?: (selectedProps: PricingCardProps) => void;
  setSlugUrlParam?: (slug: MembershipOptionType) => void;
  slug?: MembershipOptionType;
  title: string;
  hasExperimentModal?: boolean;
  modal?: ExperimentModal | Queries.ContentfulModal001;
  overrideCTAProps?: ButtonProps;
}

const RESULTS_CHECKOUT_URLS = [
  `${RESULTS_ROOT}/virtual-care/intake?program=everlywell-plus&plan=1-month`,
  `${RESULTS_ROOT}/virtual-care/intake?program=everlywell-plus&plan=12-month`,
];

// PricingSelector ------

const PricingSelector: React.FC<PricingSelectorProps> = (props) => {
  const {
    CTALabel,
    CTAUrl,
    backgroundColor,
    disclaimer,
    eyebrow,
    options,
    sectionId,
    selectedOptionCallback,
    title,
    hasExperimentModal,
    modal,
    overrideCTAProps,
    // TODO: Commented to prevent page scroll to top when changing the selected variant
    // slug,
    // setSlugUrlParam,
  } = props;

  const { handleMdpCtaClick } = useMdpModals();
  const flags = useFlags();
  const isOldMDPCheckout = flags.isOldMDPCheckout ?? false;
  const [openExperimentModal, experimentModalHandler] = useState(false);

  const { onClose } = useDisclosure();

  // Select the 1 month card by default (or whatever the first card is)
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const [checkoutUrl, setCheckoutUrl] = useState<string>();
  const [urlOptions, setUrlOptions] = useState<string[]>([]);
  const [isInitialCallbackSet, setIsInitialCallbackSet] = useState(false);

  const [isPricingSelectorOpen, setIsPricingSelectorOpen] = useState(false);

  const hasPricingSelectorModal = modal && isPricingSelectorModal(modal);

  // Check LD flag and set initial selected checkout URL
  useEffect(() => {
    const shouldRedirectToNewCheckout = !isOldMDPCheckout;
    let urlToUse = RESULTS_CHECKOUT_URLS[selectedCardIndex];
    setUrlOptions(RESULTS_CHECKOUT_URLS);

    if (shouldRedirectToNewCheckout) {
      urlToUse = options[selectedCardIndex].url;
      setUrlOptions(options.map((option) => option.url));
    }

    setCheckoutUrl(urlToUse);
  }, [selectedCardIndex, isOldMDPCheckout, options]);

  // TODO: Commented to prevent page scroll to top when changing the selected variant
  // useEffect(() => {
  //   if (slug && slug === MembershipOptionType.YearlyMembership) {
  //     setSelectedCardIndex(1);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  useEffect(() => {
    // Check if the initial callback has been called to avoid calling it again
    // Check if the checkout URL is set to avoid calling the callback before the URL is determined
    if (isInitialCallbackSet || !checkoutUrl) {
      return;
    }

    if (selectedOptionCallback) {
      selectedOptionCallback({
        ...options[selectedCardIndex],
        url: checkoutUrl,
      });
      setIsInitialCallbackSet(true);
    }
  }, [
    isInitialCallbackSet,
    options,
    selectedCardIndex,
    selectedOptionCallback,
    checkoutUrl,
  ]);

  const handleCardClick = (index: number) => {
    setSelectedCardIndex(index);

    // Call the callback when the selected card changes
    if (selectedOptionCallback) {
      selectedOptionCallback({
        ...options[index],
        url: urlOptions[index],
      });
    }

    // TODO: Commented to prevent page scroll to top when changing the selected variant
    // if (setSlugUrlParam) {
    //   if (index === 0) {
    //     setSlugUrlParam(MembershipOptionType.MonthlyMembership);
    //   } else {
    //     setSlugUrlParam(MembershipOptionType.YearlyMembership);
    //   }
    // }
  };

  const optionList = options.map((option, index) => (
    <PricingCard
      key={`pricing-card-${index}`}
      {...option}
      isSelected={index === selectedCardIndex}
      index={index}
      handleClick={handleCardClick}
      onlyChild={options.length === 1}
    />
  ));

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title,
        contentfulSection: 'PricingSelector',
        ctaText: CTALabel,
        selectedOption: options[selectedCardIndex].period,
      },
    });

    if (hasExperimentModal && options[selectedCardIndex].isInvalid) {
      event.preventDefault();
      experimentModalHandler(true);
      return;
    }

    if (hasPricingSelectorModal) {
      event.preventDefault();
      setIsPricingSelectorOpen(true);
      return;
    }

    // Keeps compatibility with the disclaimer modal
    const skipScreenerModal = shouldUrlSkipScreenerModal({ url: checkoutUrl });

    handleMdpCtaClick({
      event,
      ctaUrl: checkoutUrl || '',
      skipScreenerModal,
    });
  };

  const handleModalButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (hasExperimentModal) experimentModalHandler(false);

    let ctaUrl = '';
    let ctaLabel = '';

    if (modal && isExperimentModal(modal)) {
      ctaUrl = modal.cta?.url || '';
      ctaLabel = modal.cta?.label || '';
    }

    if (ctaUrl[0] === '#') {
      event.preventDefault();
    }

    if (ctaUrl.includes('#disclaimer')) {
      handleMdpCtaClick({ event, ctaUrl: checkoutUrl || '' });
    }

    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title,
        contentfulSection: 'PricingSelector',
        ctaText: ctaLabel,
        ctaUrl: ctaUrl,
      },
    });
  };

  const sharedButtonProps: ButtonProps = {
    variant: 'primary',
    rightIcon: <Icon as={ArrowRight} color="tints.white" />,
    id: 'pricing-section-join-cta',
    // Render a regular anchor tag if the checkout URL is a full URL
    onClick: handleButtonClick,
    ...createSharedButtonProps(checkoutUrl),
    ...overrideCTAProps,
    ...(CTAUrl && hasPricingSelectorModal ? { href: CTAUrl, as: Link } : {}),
  };

  return (
    <>
      <SectionContainer
        id={sectionId ? sectionId : ''}
        display="flex"
        justifyContent="center"
        size={['fluid', 'fluid', 'fluid', 'fluid', 'ultra']}
        paddingTop={['60px', '120px']}
        paddingBottom={['60px', '120px']}
        backgroundColor={backgroundColor ? backgroundColor : 'viridian.wash'}
        clipPath={[
          'ellipse(350% 60% at 50% 60%)',
          'ellipse(170% 60% at 50% 60%)',
        ]}
      >
        <Container
          display="flex"
          justifyContent="center"
          flexWrap="wrap"
          flexDirection="column"
          alignItems="center"
          size={['sm', 'sm', 'lg']}
        >
          <Text
            size="lg"
            width="100%"
            textAlign="center"
            color="viridian.base"
            marginBottom={['1', '2']}
          >
            {eyebrow}
          </Text>

          <Heading
            // @ts-ignore
            size={['32px', '2xl']}
            width="100%"
            textAlign="center"
            marginBottom={10}
          >
            {title}
          </Heading>

          <Box
            display="flex"
            flexDirection={['column', 'column', 'row']}
            gap={[5, 4]}
            width="100%"
            alignItems="center"
            justifyContent="center"
            marginBottom={[6, 10]}
          >
            {optionList}
          </Box>

          <Button
            {...sharedButtonProps}
            _hover={{
              textDecoration: 'none',
            }}
          >
            {CTALabel}
          </Button>
          {disclaimer && (
            <Box marginTop={[12]}>
              <Text size="sm" color="tints.darkGrey" textAlign="center">
                {disclaimer}
              </Text>
            </Box>
          )}
        </Container>
      </SectionContainer>
      {modal && isExperimentModal(modal) ? (
        <Modal isOpen={openExperimentModal} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            {modal?.title && (
              <ModalHeader color="viridian.base">{modal?.title}</ModalHeader>
            )}
            <ModalCloseButton />
            <ModalBody>
              <Flex flexDirection="column" gap="6">
                {modal?.description && (
                  <Text color="tints.darkGrey">{modal?.description}</Text>
                )}
                {modal?.cta && (
                  <Button
                    width="100%"
                    onClick={handleModalButtonClick}
                    {...(modal?.cta?.url &&
                      createSharedButtonProps(modal?.cta?.url))}
                  >
                    {modal?.cta?.label}
                  </Button>
                )}
              </Flex>
            </ModalBody>
          </ModalContent>
        </Modal>
      ) : null}

      {hasPricingSelectorModal ? (
        <PricingSelectorModal
          {...pricingModalParser(modal)}
          isOpen={isPricingSelectorOpen}
          onClose={() => setIsPricingSelectorOpen(false)}
        ></PricingSelectorModal>
      ) : null}
    </>
  );
};

// Export ------

export default PricingSelector;
